import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Clock } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [blogData, setBlogData] = useState({ posts: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch latest blog data from public folder on every render
    const fetchBlogData = async () => {
      try {
        const response = await fetch(`/data/blogData.js?t=${Date.now()}`);
        const text = await response.text();
        // Parse the JavaScript file to get the data
        const script = document.createElement('script');
        script.textContent = text;
        document.body.appendChild(script);
        
        if (window.blogData) {
          setBlogData(window.blogData);
        }
        document.body.removeChild(script);
      } catch (error) {
        console.error('Error loading blog data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  const post = blogData.posts.find(p => p.slug === slug);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-dark-600 font-body">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-display font-bold text-dark-900 mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-primary-600 hover:underline">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-50">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Back Link */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-4">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-dark-900 mb-6">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-6 text-dark-600">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="font-body">{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="font-body">{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="font-body">{post.readTime}</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className="mb-8 rounded-xl overflow-hidden shadow-soft">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-[400px] object-cover"
            onError={(e) => e.target.src = 'https://via.placeholder.com/800x400?text=Blog+Image'}
          />
        </div>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none font-body blog-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* CTA */}
        <div className="mt-12 bg-gradient-primary rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-display font-bold mb-3 text-dark-900">Ready to Shop?</h3>
          <p className="text-dark-700 mb-6 font-body">Browse our collection of affordable furniture</p>
          <Link to="/products" className="inline-block px-8 py-3 bg-white text-primary-700 rounded-lg font-semibold hover:bg-dark-50 transition-smooth">
            Shop Now
          </Link>
        </div>
      </article>

      {/* Blog Content Styles */}
      <style>{`
        .blog-content {
          line-height: 1.8;
        }
        .blog-content h2 {
          font-size: 1.75rem;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          color: #1a202c;
          font-family: 'Playfair Display', serif;
        }
        .blog-content p {
          margin-bottom: 1.5rem;
          color: #4a5568;
          font-size: 1.125rem;
        }
        .blog-content ul, .blog-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .blog-content li {
          margin-bottom: 0.75rem;
          color: #4a5568;
        }
        .blog-content strong {
          font-weight: 600;
          color: #1a202c;
        }
      `}</style>
    </div>
  );
};

export default BlogPostPage;
